<template>
  <div>
    <b-container fluid class="pb-4 px-0">
      <template v-if="apiResponse.success">
        <template v-if="items.length">
          <!-- Toolbar -->
          <div class="dir-toolbar">
            <div class="dir-toolbar-search">
              <el-input
                v-model="searchQuery"
                placeholder="Buscar empresa, email, RUC..."
                suffix-icon="el-icon-search"
                size="small"
                clearable
              />
            </div>
            <el-select
              v-model="orderByQuery"
              placeholder="Ordenar"
              size="small"
              class="dir-toolbar-select"
            >
              <el-option label="Recientes" value="recent" />
              <el-option label="Nombre A-Z" value="a-z" />
              <el-option label="Nombre Z-A" value="z-a" />
              <el-option label="Mayor facturación" value="top-revenue" />
              <el-option label="Más contactos" value="most-contacts" />
            </el-select>
            <div class="dir-toolbar-right">
              <ExportButtons
                :data="filteredClients"
                :columns="exportColumns"
                filename="directorio_empresas"
              />
              <button
                class="dir-view-toggle"
                :class="{ active: viewMode === 'grid' }"
                @click="viewMode = 'grid'"
              >
                <i class="el-icon-menu"></i>
              </button>
              <button
                class="dir-view-toggle"
                :class="{ active: viewMode === 'list' }"
                @click="viewMode = 'list'"
              >
                <i class="el-icon-s-unfold"></i>
              </button>
            </div>
          </div>

          <!-- Type filter pills (mobile: replaces sidebar filters) -->
          <div class="dir-filter-pills d-lg-none">
            <button
              class="dir-filter-pill"
              :class="{ active: typeFilter === '' }"
              @click="typeFilter = ''"
            >
              Todos
            </button>
            <button
              class="dir-filter-pill"
              :class="{ active: typeFilter === 1 }"
              @click="typeFilter = typeFilter === 1 ? '' : 1"
            >
              Clientes
            </button>
            <button
              class="dir-filter-pill"
              :class="{ active: typeFilter === 2 }"
              @click="typeFilter = typeFilter === 2 ? '' : 2"
            >
              Proveedores
            </button>
          </div>

          <!-- Grid view -->
          <div v-if="viewMode === 'grid'" class="dir-grid">
            <div
              v-for="(item, index) in filteredClients"
              :key="item.id || index"
              class="dir-card"
              @click="goRouter('business.edit', { id: item.id })"
            >
              <div class="dir-card-header">
                <div
                  class="dir-card-avatar"
                  :style="
                    !item.image ? { background: getAvatarColor(index) } : {}
                  "
                >
                  <img
                    v-if="item.image"
                    :src="item.image"
                    class="dir-card-avatar-img"
                    alt=""
                  />
                  <template v-else>{{ getInitials(item.name) }}</template>
                </div>
                <div style="flex: 1; min-width: 0;">
                  <span class="dir-card-name">{{ item.name }}</span>
                  <el-tag
                    v-if="item.contact_type"
                    size="mini"
                    :class="
                      item.contact_type === 1
                        ? 'dir-badge-client'
                        : 'dir-badge-provider'
                    "
                    effect="plain"
                    :disable-transitions="true"
                  >
                    {{ item.contact_type === 1 ? "Cliente" : "Proveedor" }}
                  </el-tag>
                </div>
              </div>

              <div v-if="item.email" class="dir-card-meta">
                <i class="el-icon-message"></i>{{ item.email }}
              </div>
              <div v-if="item.city && item.city.name" class="dir-card-meta">
                <i class="el-icon-location"></i>{{ item.city.name }}
              </div>

              <div class="dir-card-footer">
                <span class="dir-card-amount" v-if="item.invoices_earnings">
                  ${{ formatAmount(item.invoices_earnings) }}
                </span>
                <span class="dir-card-amount" v-else style="opacity: 0.4;">
                  $0.00
                </span>
                <span
                  class="dir-card-count"
                  v-if="item.contacts_count !== undefined"
                >
                  <i class="el-icon-user"></i>{{ item.contacts_count || 0 }}
                </span>
                <span class="dir-card-date">
                  {{ formatDate(item.created_at) }}
                </span>
              </div>
            </div>

            <!-- Empty state when local filter produces zero results -->
            <div
              v-if="!filteredClients.length && !loadingMore"
              class="dir-grid-empty"
            >
              Sin resultados
            </div>

            <!-- Skeleton cards inline while loading next batch -->
            <template v-if="loadingMore">
              <div
                v-for="i in 4"
                :key="'sk-g-' + i"
                class="dir-card dir-card--skeleton"
              >
                <div class="dir-card-header">
                  <div class="sk-circle"></div>
                  <div style="flex:1;min-width:0">
                    <div class="sk-bar" style="width:65%"></div>
                    <div
                      class="sk-bar sk-bar--sm"
                      style="width:38%;margin-top:6px"
                    ></div>
                  </div>
                </div>
                <div
                  class="sk-bar sk-bar--sm"
                  style="width:82%;margin-top:14px"
                ></div>
                <div
                  class="sk-bar sk-bar--sm"
                  style="width:58%;margin-top:8px"
                ></div>
                <div class="dir-card-footer">
                  <div class="sk-bar" style="width:72px"></div>
                  <div class="sk-bar" style="width:32px"></div>
                </div>
              </div>
            </template>
          </div>

          <!-- List view (tabla horizontal) -->
          <div v-else class="dir-table">
            <div class="dir-table-header">
              <span class="dir-th-name">Empresa</span>
              <span class="dir-th-type">Tipo</span>
              <span class="dir-th-email">Email</span>
              <span class="dir-th-amount">Facturado</span>
              <span class="dir-th-date">Creado</span>
              <span class="dir-th-action"></span>
            </div>
            <div
              v-for="(item, index) in filteredClients"
              :key="item.id || 'list-' + index"
              class="dir-table-row"
              @click="goRouter('business.edit', { id: item.id })"
            >
              <span class="dir-td-name">
                <div
                  class="dir-row-avatar"
                  :style="{ background: getAvatarColor(index) }"
                >
                  {{ getInitials(item.name) }}
                </div>
                <span class="dir-row-name-text">{{ item.name }}</span>
              </span>
              <span class="dir-td-type">
                <el-tag
                  v-if="item.contact_type"
                  size="mini"
                  :class="
                    item.contact_type === 1
                      ? 'dir-badge-client'
                      : 'dir-badge-provider'
                  "
                  effect="plain"
                  :disable-transitions="true"
                >
                  {{ item.contact_type === 1 ? "Cliente" : "Proveedor" }}
                </el-tag>
              </span>
              <span class="dir-td-email">{{ item.email || "—" }}</span>
              <span class="dir-td-amount"
                >${{ formatAmount(item.invoices_earnings) }}</span
              >
              <span class="dir-td-date">{{ formatDate(item.created_at) }}</span>
              <span class="dir-td-action" @click.stop>
                <button
                  class="dir-row-search-btn"
                  @click="goRouter('business.edit', { id: item.id })"
                >
                  <i class="el-icon-search"></i>
                </button>
              </span>
            </div>
            <!-- Skeleton rows inline while loading next batch -->
            <template v-if="loadingMore">
              <div
                v-for="i in 4"
                :key="'sk-l-' + i"
                class="dir-table-row dir-row--skeleton"
              >
                <span class="dir-td-name">
                  <div class="sk-circle sk-circle--sm"></div>
                  <div class="sk-bar" style="width:110px"></div>
                </span>
                <span class="dir-td-type">
                  <div class="sk-bar" style="width:55px"></div>
                </span>
                <span class="dir-td-email">
                  <div class="sk-bar" style="width:130px"></div>
                </span>
                <span class="dir-td-amount">
                  <div class="sk-bar" style="width:65px"></div>
                </span>
                <span class="dir-td-date">
                  <div class="sk-bar" style="width:55px"></div>
                </span>
                <span class="dir-td-action"></span>
              </div>
            </template>

            <div
              v-if="!filteredClients.length && !loadingMore"
              class="dir-table-empty"
            >
              Sin resultados
            </div>
          </div>
        </template>
        <!-- When a search returns 0 results, show a specific message rather than
           the generic "no companies registered" empty state -->
        <div v-else-if="filters.search" class="dir-search-empty">
          <i
            class="el-icon-search"
            style="font-size:32px;color:#c0c4cc;display:block;margin-bottom:12px;"
          ></i>
          <p>
            No se encontraron empresas para
            <strong>« {{ filters.search }} »</strong>
          </p>
          <p style="color:#c0c4cc;font-size:13px;">
            Intenta con otro nombre, email o RUC.
          </p>
        </div>
        <EmptyPage
          v-else
          icon="el-icon-office-building"
          text="Aún no has registrado empresas, empieza a crear tu lista de contactos para ofrecer tus productos o servicios."
        />

        <!-- Scroll sentinel: when visible, next page loads -->
        <div ref="scrollSentinel" class="dir-scroll-sentinel"></div>
      </template>
      <SkeletonLoadingCard v-else />
    </b-container>
  </div>
</template>

<script>
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import EmptyPage from "../../components/EmptyPage";
import businessService from "../services/businessService";

const AVATAR_COLORS = [
  "#31355c",
  "#3fdbbf",
  "#6366f1",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#06b6d4"
];

export default {
  name: "BusinessListComponent",
  props: {
    filterType: { type: String, default: "" }
  },
  data: () => ({
    apiResponse: { success: false },
    searchQuery: "",
    orderByQuery: "recent",
    typeFilter: "",
    viewMode: "grid",
    items: [],
    hasMore: true,
    loadingPage: false,
    loadingMore: false,
    filters: { page: 1, search: null },
    searchTimer: null
  }),
  computed: {
    filteredClients() {
      let clients = this.items.concat();

      // Filter by type (mobile select — numeric) or sidebar (string "clients"/"providers")
      const activeType =
        this.typeFilter ||
        (this.filterType === "clients"
          ? 1
          : this.filterType === "providers"
          ? 2
          : "");
      if (activeType) {
        clients = clients.filter(
          c => Number(c.contact_type) === Number(activeType)
        );
      }

      // Sort
      clients.sort((a, b) => {
        switch (this.orderByQuery) {
          case "recent":
            return (b.id || 0) - (a.id || 0);
          case "old":
            return (a.id || 0) - (b.id || 0);
          case "a-z":
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          case "z-a":
            return (b.name || "")
              .toLowerCase()
              .localeCompare((a.name || "").toLowerCase());
          case "top-revenue":
            return (b.invoices_earnings || 0) - (a.invoices_earnings || 0);
          case "most-contacts":
            return (b.contacts_count || 0) - (a.contacts_count || 0);
          default:
            return 0;
        }
      });

      return clients;
    },
    apiContactType() {
      if (this.filterType === "clients") return 1;
      if (this.filterType === "providers") return 2;
      return undefined;
    },
    exportColumns() {
      return [
        { label: "Nombre", field: "name" },
        { label: "Nombre comercial", field: "tradename" },
        {
          label: "Tipo",
          value: i =>
            Number(i.contact_type) === 1
              ? "Cliente"
              : Number(i.contact_type) === 2
              ? "Proveedor"
              : ""
        },
        {
          label: "Tipo documento",
          value: i =>
            i.document_type == 1
              ? "RUC"
              : i.document_type == 2
              ? "Cédula"
              : i.document_type == 4
              ? "Consumidor final"
              : i.document_type == 5
              ? "Pasaporte"
              : ""
        },
        { label: "Documento", field: "document" },
        { label: "Email", field: "email" },
        { label: "Teléfono", field: "phone" },
        { label: "Dirección", field: "address" },
        { label: "Ciudad", value: i => (i.city ? i.city.name : "") },
        { label: "Web", field: "web" },
        { label: "Total contactos", value: i => i.contacts_count || 0 },
        { label: "Facturación total", value: i => i.invoices_earnings || 0 }
      ];
    }
  },
  watch: {
    filterType(val) {
      if (val === "recent") {
        this.orderByQuery = "recent";
      }
      this.fetchFirstPage();
    },
    viewMode(val) {
      this.$store.dispatch("setViewPreference", {
        key: "directory",
        value: val
      });
    },
    searchQuery() {
      this.onSearchDebounced();
    }
  },
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    getInitials(name) {
      if (!name) return "??";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },
    getAvatarColor(index) {
      return AVATAR_COLORS[index % AVATAR_COLORS.length];
    },
    formatAmount(value) {
      if (!value) return "0.00";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic"
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    // Load page 1 directly (not via infinite-loading) so the sentinel starts
    // below the fold when infinite-loading mounts. This prevents cascade loading.
    // When a contact-type filter is active the backend does NOT support filtering,
    // so we fetch ALL pages and let the computed `filteredClients` handle it.
    async fetchFirstPage() {
      this.apiResponse.success = false;
      this.items = [];
      this.hasMore = true;
      this.loadingPage = false;
      this.loadingMore = false;

      // If a type filter is active (clients/providers), load everything
      if (this.apiContactType) {
        await this.fetchAllPages();
        return;
      }

      try {
        const response = await businessService.getBusiness({
          params: {
            page: 1,
            per_page: 20,
            name: this.filters.search || undefined
          }
        });
        if (response.data && response.data.clients) {
          const { clients, pagination } = response.data;
          this.items = clients;
          const total =
            pagination && pagination.total != null
              ? pagination.total
              : clients.length;
          this.$emit("update-count", total);
          this.filters.page = 2;
          this.hasMore = !(pagination && 1 >= pagination.last_page);
        } else {
          this.hasMore = false;
        }
      } catch {
        this.hasMore = false;
      } finally {
        this.apiResponse.success = true;
      }
    },
    // Fetch all pages when backend can't filter by contact_type
    async fetchAllPages() {
      try {
        let page = 1;
        let lastPage = 1;
        let allClients = [];
        do {
          const response = await businessService.getBusiness({
            params: {
              page,
              per_page: 100,
              name: this.filters.search || undefined
            }
          });
          if (response.data && response.data.clients) {
            const { clients, pagination } = response.data;
            allClients = allClients.concat(clients);
            lastPage = pagination ? pagination.last_page : 1;
          } else {
            break;
          }
          page++;
        } while (page <= lastPage);
        this.items = allClients;
        this.hasMore = false;
        const filtered = allClients.filter(
          c => Number(c.contact_type) === Number(this.apiContactType)
        );
        this.$emit("update-count", filtered.length);
      } catch {
        this.hasMore = false;
      } finally {
        this.apiResponse.success = true;
      }
    },
    // Native scroll listener — fires when user scrolls within 400px of the sentinel.
    // Fully replaces vue-infinite-loading to avoid timing/cascade issues.
    onWindowScroll() {
      if (!this.hasMore || this.loadingPage || !this.apiResponse.success)
        return;
      const sentinel = this.$refs.scrollSentinel;
      // offsetParent is null when the element (or an ancestor) has display:none
      if (!sentinel || !sentinel.offsetParent) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 400) {
        this.loadNextPage();
      }
    },
    async loadNextPage() {
      if (!this.hasMore || this.loadingPage) return;
      // Set synchronously (before any await) so rapid scroll events can't
      // slip through the guard above before this function yields.
      this.loadingPage = true;
      this.loadingMore = true;
      try {
        const response = await businessService.getBusiness({
          params: {
            page: this.filters.page,
            per_page: 20,
            name: this.filters.search || undefined,
            contact_type: this.apiContactType
          }
        });
        if (response.data && response.data.clients) {
          const { clients, pagination } = response.data;
          const existingIds = new Set(this.items.map(i => i.id));
          const newItems = clients.filter(c => !existingIds.has(c.id));
          this.items = [...this.items, ...newItems];
          const done = pagination && this.filters.page >= pagination.last_page;
          if (!done) this.filters.page++;
          else this.hasMore = false;
        } else {
          this.hasMore = false;
        }
      } catch {
        // keep hasMore=true so user can retry by scrolling again
      } finally {
        this.loadingPage = false;
        this.loadingMore = false;
      }
    },
    onSearchDebounced() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.filters.search = this.searchQuery;
        this.filters.page = 1;
        this.fetchFirstPage();
      }, 350);
    },
    dateNow() {
      return new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "_");
    },
    async downloadExcel() {
      this.loading = true;
      try {
        const response = await businessService.downloadExcel();
        const { data, success } = response.data;
        if (success) {
          const date = this.dateNow();
          const anchor = document.createElement("a");
          const filename = `${date}_listado_empresas.xlsx`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "Excel descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      } finally {
        this.loading = false;
      }
    },
    async downloadPdf() {
      this.loading = true;
      try {
        const response = await businessService.downloadPdf();
        const { data, success } = response.data;
        if (success) {
          const date = this.dateNow();
          const anchor = document.createElement("a");
          const filename = `${date}_listado_empresas.pdf`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "PDF descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      } finally {
        this.loading = false;
      }
    }
  },
  components: {
    SkeletonLoadingCard,
    EmptyPage
  },
  mounted() {
    const saved = this.$store.getters.getViewPreference("directory");
    if (saved) this.viewMode = saved;
    this.fetchFirstPage();
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onWindowScroll);
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$border: #e4eaf6;
$bg: #f7f8f9;

// ── Skeleton shimmer ──
@keyframes sk-shine {
  from {
    background-position: -400px 0;
  }
  to {
    background-position: 400px 0;
  }
}
.sk-bar,
.sk-circle {
  display: block;
  background: linear-gradient(90deg, #e9ebee 25%, #f3f3f3 50%, #e9ebee 75%);
  background-size: 800px 100%;
  animation: sk-shine 1.4s ease-in-out infinite;
  border-radius: 6px;
}
.sk-bar {
  height: 13px;
  &--sm {
    height: 10px;
  }
}
.sk-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  &--sm {
    width: 30px;
    height: 30px;
  }
}
.dir-card--skeleton {
  pointer-events: none;
  cursor: default !important;
  &:hover {
    box-shadow: none !important;
    transform: none !important;
  }
}
.dir-row--skeleton {
  pointer-events: none;
  cursor: default !important;
  &:hover {
    background: transparent !important;
  }
}

.dir-toolbar .el-input__inner,
.dir-toolbar .el-select .el-input__inner {
  min-height: 34px !important;
  height: 34px;
  font-size: 13px;
  border-radius: 8px;
}

// ── LIST VIEW (tabla horizontal) ──
.dir-table {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  overflow: hidden;
}
.dir-table-header {
  display: grid;
  grid-template-columns: 1fr 90px 200px 120px 100px 44px;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: $bg;
  border-bottom: 1px solid $border;
  font-size: 12px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.dir-table-row {
  display: grid;
  grid-template-columns: 1fr 90px 200px 120px 100px 44px;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid $border;
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  transition: background 0.12s;
  min-height: 56px;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
}
.dir-td-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.dir-row-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}
.dir-row-name-text {
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dir-td-email {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dir-td-location {
  font-size: 12px;
  color: #909399;
}
.dir-td-amount {
  font-weight: 600;
  color: $primary;
}
.dir-td-contacts {
  text-align: center;
  color: #909399;
}
.dir-td-date {
  font-size: 12px;
  color: #909399;
}
.dir-table-empty {
  padding: 40px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}
.dir-scroll-sentinel {
  height: 1px;
  width: 100%;
}
.dir-grid-empty {
  grid-column: 1 / -1;
  padding: 48px 20px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}
.dir-search-empty {
  padding: 60px 20px;
  text-align: center;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  p {
    margin: 0 0 6px;
  }
}

.dir-row-search-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #3ecfb4;
  color: #fff;
  i {
    color: #fff;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s, transform 0.1s;
  &:hover {
    background: #1d9e75;
    transform: scale(1.08);
  }
  i {
    color: #fff;
  }
}
.dir-td-action {
  display: flex;
  justify-content: center;
}
@media (max-width: 1024px) {
  .dir-table-header,
  .dir-table-row {
    grid-template-columns: 1fr 80px 160px 100px 44px;
  }
  .dir-th-date,
  .dir-td-date {
    display: none;
  }
}
@media (max-width: 768px) {
  .dir-table-header,
  .dir-table-row {
    grid-template-columns: 1fr 80px 90px 44px;
  }
  .dir-th-email,
  .dir-td-email,
  .dir-th-date,
  .dir-td-date {
    display: none;
  }
}
@media (max-width: 480px) {
  .dir-table-header,
  .dir-table-row {
    grid-template-columns: 1fr 80px 44px;
  }
  .dir-th-amount,
  .dir-td-amount,
  .dir-th-email,
  .dir-td-email,
  .dir-th-date,
  .dir-td-date {
    display: none;
  }
}
@media (max-width: 360px) {
  .dir-table-header,
  .dir-table-row {
    grid-template-columns: 1fr 44px;
  }
  .dir-th-type,
  .dir-td-type,
  .dir-th-amount,
  .dir-td-amount,
  .dir-th-email,
  .dir-td-email,
  .dir-th-date,
  .dir-td-date {
    display: none;
  }
}
</style>
